import { useState, useRef, useCallback } from "react";
import { message } from "antd";
import { usePutPostAPI } from "utilFunctions/CustomHooks/usePutPostAPI";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import {
  deleteAPI,
  getAPIRequest,
} from "utilFunctions/Axios/AxiosRequestConfiguration";
import { BASE_URL } from "utilFunctions/Constants/urlConstants";
import getRequestInterface from "utilFunctions/Axios/getRequestInterface";
import {
  getObjectKeyFromPresignedUrl,
  isPdfFile,
  normalizeFileName,
} from "utilFunctions/shared/fileUtils";
import { uploadFileToS3 } from "utilFunctions/shared/uploadFileToS3";

export const useChannelsPost = (
  form?: any,
  options: { onSuccess?: (data: any) => void; selectedPostForUpdate?: any } = {}
) => {
  const { onSuccess, selectedPostForUpdate } = options;
  const [posts, setPosts] = useState<any[]>([]);
  const [isPostLoading, setIsPostLoading] = useState(false);
  const [isSavingPost, setIsSavingPost] = useState(false);
  const savingPostRef = useRef(false);
  const abortControllersRef = useRef(new Map());
  const currentFilesRef = useRef<any[]>([]);
  const currentFormDataRef = useRef<any>(null);
  const offset = useRef(0);
  const [hasMore, setHasMore] = useState(false);
  const loadedPostIds = useRef(new Set());
  const LIMIT = 10;

  const finishSavingPost = () => {
    savingPostRef.current = false;
    setIsSavingPost(false);
  };

  const onFetchSuccess = useCallback(
    (data: any) => {
      console.log("Fetched posts data:", data);
      if (data?.data && data.data.length > 0) {
        const newData =
          offset.current === 0 ? data.data : [...posts, ...data.data];

        console.log("Fetched posts data:", newData);
        setPosts(newData);
        setHasMore(true);
      } else {
        console.log("Fetched posts data:");
        setHasMore(false);
        if (offset.current === 0) {
          setPosts([]);
          loadedPostIds.current.clear();
        }
      }
      setIsPostLoading(false);
    },
    [posts, setPosts, setHasMore, setIsPostLoading, LIMIT]
  );

  const onFetchError = useCallback(
    (error: any) => {
      message.error(error.message);
      setIsPostLoading(false);
    },
    [setIsPostLoading]
  );

  const { makeRequest: fetchPosts, loading: postsLoading } = useGetApi(
    onFetchSuccess,
    onFetchError
  );

  const loadPosts = useCallback(
    (url: string, isRefresh = false) => {
      if (isRefresh) {
        offset.current = 0;
      }
      setIsPostLoading(true);
      fetchPosts(`${url}&offset=${offset.current}&limit=${LIMIT}`);
    },
    [fetchPosts]
  );

  const postsLoadingRef = useRef(postsLoading);
  postsLoadingRef.current = postsLoading;

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  const next = useCallback(
    (url: string) => {
      if (postsLoadingRef.current || !hasMoreRef.current) return;
      offset.current += LIMIT;
      loadPosts(url);
    },
    [loadPosts]
  );

  const { makeRequest: createPost, loading: createPostLoading } = usePutPostAPI(
    (data) => {
      finishSavingPost();
      message.success("Created successfully!");
      if (onSuccess) onSuccess(data.data);
    },
    (error) => {
      finishSavingPost();
      message.error(error.message || "Failed to create post.");
    },
    "POST"
  );

  const { makeRequest: updatePost, loading: updatePostLoading } = usePutPostAPI(
    (data) => {
      finishSavingPost();
      console.log("Post updated:", data);
      message.success("Updated successfully!");
      if (onSuccess) onSuccess(selectedPostForUpdate);
    },
    (error) => {
      finishSavingPost();
      message.error(error.message || "Failed to update post.");
    },
    "PUT"
  );
  const { makeRequest: updatePostPublish, loading: updatePostPublishLoading } =
    usePutPostAPI(
      (data) => {
        console.log("Post updated:", data);
        message.success("Published successfully!");
      },
      (error) => {
        message.error(error.message || "Failed to update post.");
      },
      "PUT"
    );

  const updateFileStatus = (file: any, status: any, response?: any) => {
    if (form) {
      const { Image, Ebook, videos } = form.getFieldsValue([
        "Image",
        "Ebook",
        "videos",
      ]);
      const update = (fileList: any) =>
        fileList?.map((f: any) =>
          f.uid === file.uid ? { ...f, status, response } : f
        );
      form.setFieldsValue({
        Image: update(Image),
        Ebook: update(Ebook),
        videos: update(videos),
      });
    }
  };
  const processFileUploads = async (
    filesToUpload: any[],
    presignedUrls: any[]
  ) => {
    console.log("Processing file uploads", filesToUpload, presignedUrls);
    const usedFileUids = new Set<string>();
    const results: any[] = [];

    // Upload sequentially to keep WebKit from buffering several request bodies
    // at once. This is especially important when files come from iCloud/Photos.
    for (let index = 0; index < presignedUrls.length; index += 1) {
      const presignedData = presignedUrls[index];
      let originalFile: any;
      let controller: AbortController | undefined;
      try {
        const normalizedServerName = normalizeFileName(presignedData.fileName);
        originalFile =
          filesToUpload.find(
            (file: any) =>
              !usedFileUids.has(file.uid) &&
              normalizeFileName(file.name) === normalizedServerName
          ) ||
          filesToUpload.find(
            (file: any) =>
              !usedFileUids.has(file.uid) &&
              (isPdfFile(file) ? "Ebook" : "Image") === presignedData.assetType
          ) ||
          filesToUpload.find((file: any) => !usedFileUids.has(file.uid));

        if (!originalFile) {
          message.error(
            `Could not find original file for ${presignedData.fileName}`
          );
          results.push(null);
          continue;
        }
        usedFileUids.add(originalFile.uid);

        const browserFile =
          (originalFile.originFileObj as File | undefined) ||
          (originalFile as File);

        controller = new AbortController();
        abortControllersRef.current.set(originalFile.uid, controller);

        await uploadFileToS3(
          browserFile,
          presignedData.preSignedUrl,
          controller.signal
        );
        updateFileStatus(originalFile, "done");
        results.push({
          assetType: presignedData.assetType,
          url: getObjectKeyFromPresignedUrl(presignedData.preSignedUrl),
          id: presignedData.contentRecordId,
          name: originalFile.name,
        });
      } catch (error: any) {
        const reason = controller?.signal.aborted
          ? "Upload canceled"
          : error.message || "Upload failed";
        if (originalFile) updateFileStatus(originalFile, "error", reason);
        console.error(`Error uploading file ${presignedData.fileName}:`, error);
        message.error(`${presignedData.fileName}: ${reason}`);
        results.push(null);
      } finally {
        if (originalFile) abortControllersRef.current.delete(originalFile.uid);
      }
    }

    // Filter out failed uploads (null values) and return only successful ones
    const successfulUploads = results.filter((result: any) => result !== null);

    console.log(
      `Upload completed: ${successfulUploads.length}/${results.length} files uploaded successfully`
    );

    if (successfulUploads.length < results.length) {
      throw new Error(
        "Some attachments failed to upload. Please retry or remove them before saving."
      );
    }

    return successfulUploads;
  };

  const createPostWithAttachments = useCallback(
    (url: string, payload: any) => {
      if (savingPostRef.current) return;
      savingPostRef.current = true;
      setIsSavingPost(true);
      console.log("Creating post with attachments. Payload:", payload);
      const filesToUpload = payload.attachments || [];
      currentFormDataRef.current = payload.formData || {};
      currentFilesRef.current = filesToUpload;

      if (filesToUpload.length > 0) {
        filesToUpload.forEach((file: any) =>
          updateFileStatus(file, "uploading")
        );
        const selectedFiles = filesToUpload.map((file: any) => ({
          fileName: file.name,
          assetType: isPdfFile(file) ? "Ebook" : "Image",
        }));
        getChannelPresignedUrls(url, selectedFiles);
      } else {
        handleFinalSubmission(payload.formData, []);
      }
    },
    [form]
  );

  const handleCancelUpload = useCallback((fileToCancel: any) => {
    console.log("Attempting to cancel upload for file:", fileToCancel);
    const controller = abortControllersRef.current.get(fileToCancel.uid);
    if (controller) {
      console.log("Cancelling upload for file UID:", fileToCancel.uid);
      controller.abort();
      abortControllersRef.current.delete(fileToCancel.uid);
      message.info(`Upload cancelled for ${fileToCancel.name}`);
    } else {
      console.warn("No controller found for file:", fileToCancel);
    }
  }, []);

  const handleFinalSubmission = (formData: any, attachments: any[]) => {
    const finalPayload = {
      ...formData,
      content: attachments,
    };

    if (selectedPostForUpdate && selectedPostForUpdate.id) {
      finalPayload.id = selectedPostForUpdate.id;
      updatePost(`${BASE_URL}post`, finalPayload);
    } else {
      createPost(`${BASE_URL}post`, finalPayload);
    }
  };

  const { makeRequest: getChannelPresignedUrls } = usePutPostAPI(
    async (data) => {
      try {
        if (
          !Array.isArray(data?.data) ||
          data.data.length !== currentFilesRef.current.length
        ) {
          throw new Error(
            "Could not prepare all attachments. Please try saving again."
          );
        }
        if (currentFilesRef.current.length > 0) {
          const attachments = await processFileUploads(
            currentFilesRef.current,

            data.data
          );

          console.log(
            "File upload process completed. Attachments:",

            attachments
          );

          handleFinalSubmission(
            { ...currentFormDataRef.current },

            attachments
          );
        } else {
          handleFinalSubmission({ ...currentFormDataRef.current }, []);
        }
      } catch (error: any) {
        finishSavingPost();
        message.error(
          error.message || "Failed to upload files. Please try again."
        );

        currentFilesRef.current.forEach((file) =>
          updateFileStatus(file, "error", "Upload failed")
        );
      } finally {
        // Clear pending data

        currentFilesRef.current = [];

        currentFormDataRef.current = null;
      }
    },
    (error) => {
      finishSavingPost();
      message.error("Failed to get pre-signed URLs.");
      console.error("Error getting pre-signed URLs:", error);
      if (currentFilesRef.current.length > 0) {
        currentFilesRef.current.forEach((file) =>
          updateFileStatus(file, "error", "Pre-signed URL failed")
        );
      }
      // Clear pending data
      currentFilesRef.current = [];
      currentFormDataRef.current = null;
    },
    "POST"
  );

  const deletePost = (postId: string, onSuccess: () => void) => {
    deleteAPI({
      url: `${BASE_URL}post/${postId}`,
      success: () => {
        message.success("Deleted successfully!");
        onSuccess();
      },
      failure: (error: any) => {
        message.error(error.response?.data?.message || "Failed to delete");
      },
    });
  };

  const getSinglePost = async (
    postId: string,
    callback: (post: any) => void
  ) => {
    setIsPostLoading(true);
    await getAPIRequest({
      url: `${BASE_URL}post/${postId}`,
      success: (response: any) => {
        if (response.data.data) {
          const postData = response.data.data;
          const content = Array.isArray(postData.content)
            ? postData.content
            : [];
          console.log("Fetched single post data:", postData, content);
          const formattedPost = {
            ...postData,
            batchIds: postData.batchIds.map((b: any) => b.batchId),
            initialBatchOptions: postData.batchIds.map((b: any) => ({
              label: b.batchName,
              value: b.batchId,
            })),
            // Image: content.filter((c: any) => c.assetType === "Image"),
            // Ebook: content.filter((c: any) => c.assetType === "Ebook"),
            // videos: content.filter((c: any) => c.assetType === "videos"),
          };
          callback(formattedPost);
          setIsPostLoading(false);
        }
      },
      failure: (err: any) => {
        setIsPostLoading(false);
        message.error(err?.message);
      },
    } as getRequestInterface);
  };

  const deleteAttachment = (
    contentRecordId: string,
    postId: string,
    onSuccess: () => void
  ) => {
    deleteAPI({
      url: `${BASE_URL}post/content`,
      success: () => {
        message.success("Attachment deleted successfully!");
        onSuccess();
      },
      failure: (error: any) => {
        message.error(
          error.response?.data?.message || "Failed to delete attachment"
        );
      },
      body: {
        contentRecordId: contentRecordId,
        postId: postId,
      },
    });
  };

  return {
    posts,
    setPosts,
    isPostLoading,
    isSavingPost,
    fetchPosts: loadPosts,
    postsLoading,
    createPost,
    createPostLoading,
    updatePost,
    updatePostLoading,
    updatePostPublish,
    updatePostPublishLoading,
    createPostWithAttachments,
    handleCancelUpload,
    deletePost,
    deleteAttachment,
    getSinglePost,
    hasMore,
    next,
  };
};
